import {
  Activite,
  payloadTimeline,
  ProchaineActivite,
  JopContext,
} from "@/models";
import TimelineService from "@/services/timeline.service";
import { Module, VuexModule, Action, Mutation } from "vuex-class-modules";

/** Holds the main activity timeline data: next activities, filtered list, and unfiltered admin list. */
@Module
export class Timeline extends VuexModule {
  private _prochaineActivite: ProchaineActivite[] | null = null;
  private _activites: Activite[] | null = null;
  private _activitesList: Activite[] | null = null;
  private _jop: JopContext | null = null;

  @Mutation
  private setProchaineActivite(activite: ProchaineActivite[]) {
    this._prochaineActivite = activite;
  }

  @Mutation
  private setActivites(activites: Activite[]) {
    this._activites = activites;
  }

  @Mutation
  private setActivitesList(activites: Activite[]) {
    this._activitesList = activites;
  }

  @Mutation
  private setJop(jop: JopContext) {
    this._jop = jop;
  }

  /** Loads the current user's next upcoming activities. See {@link getProchaineActivite}. */
  @Action
  public async prochaineActivite(): Promise<void> {
    const activite = await await TimelineService.prochaineActivite();
    this.setProchaineActivite(activite);
  }

  /**
   * Loads activities matching the given types, then filters them client-side by
   * `payload.list`: `"ALL"` keeps everything, `"NON_REPONDU"` keeps only open activities the
   * user hasn't responded to, anything else filters by that exact sign-up status. See {@link getActivites}.
   */
  @Action
  public async activites(payload: payloadTimeline): Promise<void> {
    const activites = await await TimelineService.activites(payload.types);
    if (payload.list === "ALL") {
      this.setActivites(activites);
    } else if (payload.list === "NON_REPONDU") {
      this.setActivites(
        activites.filter(
          (a) =>
            a.info_secouriste?.statut === payload.list && a.etat?.id === "OPEN"
        )
      );
    } else {
      this.setActivites(
        activites.filter((a) => a.info_secouriste?.statut === payload.list)
      );
    }
  }

  /** Loads the full, unfiltered activity list (admin/management views). See {@link getActivitesList}. */
  @Action
  public async activitesList(): Promise<void> {
    const activites = await await TimelineService.activitesList();
    this.setActivitesList(activites);
  }

  /** @deprecated Leftover from the removed JOP2024/"V3" feature — not called by any view. */
  @Action
  public async jop(): Promise<void> {
    const jop = await await TimelineService.jop();
    this.setJop(jop);
  }

  /** Triggers a bulk re-import of activities from the source system. */
  @Action
  public async importActivites(): Promise<boolean> {
    return await TimelineService.importActivites();
  }

  get getProchaineActivite(): ProchaineActivite[] | null {
    return this._prochaineActivite;
  }

  get getActivites(): Activite[] | null {
    return this._activites;
  }

  get getActivitesList(): Activite[] | null {
    return this._activitesList;
  }

  get getJop(): JopContext | null {
    return this._jop;
  }
}
